import React, { FC, useState } from 'react';
import { Field, Form, Formik, FieldProps, FormikHelpers, FormikErrors } from 'formik';

import { PasswordUpdateFormData } from 'types';
import { composeValidators, pattern, required, passwordRegex } from 'utils/validate';
import Button from 'components/Button/Button';
import InputField from 'components/form/Input/InputField';
import styles from './PasswordUpdateForm.module.scss';
import formStyles from 'pages/Login/components/LoginForm/LoginForm.module.scss';
import { ReactComponent as Eye } from 'icons/eye.svg';
import { ReactComponent as EyeOff } from 'icons/eye-off.svg';

interface Props {
  appearance?: 'default' | 'login';
  onSubmit: (values: PasswordUpdateFormData) => Promise<any>;
}

interface FormData extends PasswordUpdateFormData {
  repeatPassword: string;
}

const validate = (values: FormData) => {
  const errors = {} as FormikErrors<FormData>;
  if (values.password && values.repeatPassword !== values.password) {
    errors.repeatPassword = 'Пароли не совпадают';
  }
  return errors;
};

export const PasswordUpdateForm: FC<Props> = ({ onSubmit, appearance = 'default' }) => {
  const [visible, setVisible] = useState({ password: false, repeatPassword: false });
  const handleSubmit = async ({ password }: FormData, { setFieldError }: FormikHelpers<FormData>) => {
    try {
      await onSubmit({ password });
    } catch (e) {
      setFieldError('password', 'Ошибка');
      setFieldError('repeatPassword', 'Ошибка');
    }
  };

  return (
    <Formik
      validate={validate}
      validateOnMount={appearance === 'login'}
      initialValues={{
        password: '',
        repeatPassword: '',
      }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid }) => appearance === 'login' ? (
        <Form className={formStyles.form} noValidate>
          {(['password', 'repeatPassword'] as const).map(name => (
            <Field key={name} name={name} validate={name === 'password'
              ? composeValidators([required(), pattern(passwordRegex, 'Невалидный пароль')])
              : required()}>
              {({ field, meta }: FieldProps) => {
                const error = meta.touched && meta.error;
                return (
                  <div className={formStyles.field}>
                    <label htmlFor={`recovery-${name}`}>
                      {name === 'password' ? 'Новый пароль' : 'Повторите новый пароль'}<span className={formStyles.required}>*</span>
                    </label>
                    <div className={formStyles.passwordField}>
                      <input
                        {...field}
                        id={`recovery-${name}`}
                        type={visible[name] ? 'text' : 'password'}
                        autoComplete="new-password"
                        autoFocus={name === 'password'}
                        placeholder={name === 'password' ? 'Введите пароль' : 'Повторите пароль'}
                        required
                        className={formStyles.input}
                        aria-invalid={!!error}
                        aria-describedby={`recovery-${name}-error`}
                      />
                      <button
                        type="button"
                        className={formStyles.togglePassword}
                        onClick={() => setVisible(previous => ({ ...previous, [name]: !previous[name] }))}
                        aria-label={`${visible[name] ? 'Скрыть' : 'Показать'} ${name === 'password' ? 'новый пароль' : 'повтор пароля'}`}
                        aria-pressed={visible[name]}
                      >
                        {visible[name] ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                      </button>
                    </div>
                    <div id={`recovery-${name}-error`} className={formStyles.error} aria-live="polite">{error || ' '}</div>
                  </div>
                );
              }}
            </Field>
          ))}
          <button className={formStyles.submitBtn} type="submit" disabled={!isValid || isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Сохраняем…' : 'Подтвердить'}
          </button>
        </Form>
      ) : (
        <Form>
          <Field
            name="password"
            validate={composeValidators([
              required(),
              pattern(passwordRegex, 'Невалидный пароль')
            ])}
          >
            {(fieldProps: FieldProps) => <InputField
              htmlType="password"
              {...fieldProps}
              autoFocus
              label="Новый пароль"
              placeholder={'Введите пароль'}
            />}
          </Field>
          <Field name="repeatPassword">
            {(fieldProps: FieldProps) => <InputField
              htmlType="password"
              {...fieldProps}
              label="Подтвердить пароль"
              placeholder={'Введите пароль'}
            />}
          </Field>
          <div className={styles.submit}>
            <Button
              className={styles.submitBtn}
              htmlType="submit"
              disabled={!isValid || isSubmitting}
            >
              Подтвердить
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
